import { useState, useRef, useEffect } from "react";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { beginRequest, pendingRequest, finishRequest, rejectedRequest } from "@/lib/idempotency";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Download, History, ArrowLeft,
} from "lucide-react";

const CONFIRM_ENDPOINT = "/product-imports/confirm";
export const IMPORT_WAIT_MS = 45000;

const FIELD_LABELS = { codigo: "Código", descricao: "Descrição", familia: "Família", estoque: "Estoque", unidade: "Unidade", preco: "Preço", situacao: "Situação" };

export function ImportProducts({ open, onOpenChange, onDone }) {
  const { user } = useAuth();
  const [view, setView] = useState("select"); // select | preview | busy | result | history | detail
  const [analysis, setAnalysis] = useState(null);
  const [fileName, setFileName] = useState("");
  const [mapping, setMapping] = useState({});
  const [ignoreInactive, setIgnoreInactive] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [syncStock, setSyncStock] = useState(false);
  const [pending, setPending] = useState(null);
  const [result, setResult] = useState(null);
  const [hist, setHist] = useState(null);
  const [detail, setDetail] = useState(null);
  const fileRef = useRef(null);
  const busyRef = useRef(false);

  const attemptRef = useRef(0);
  const timerRef = useRef(null);
  const userId = user?.id;
  const tenantId = user?.tenant_id;

  useEffect(() => {
    busyRef.current = false;
    if (!open) {
      setView("select"); setAnalysis(null); setResult(null); setFileName(""); setSyncStock(false);
    } else {
      const saved = userId ? pendingRequest({ id: userId, tenant_id: tenantId }, CONFIRM_ENDPOINT) : null;
      setPending(saved);
      setView(saved ? "recovery" : "select");
    }
    return () => {
      // Closing, unmounting or changing identity invalidates only this UI wait.
      // The backend may still finish; its durable request must remain available.
      attemptRef.current += 1;
      clearTimeout(timerRef.current);
      busyRef.current = false;
    };
  }, [open, userId, tenantId]);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || busyRef.current || !user) return;
    const saved = pendingRequest(user, CONFIRM_ENDPOINT);
    if (saved) { setPending(saved); setView("recovery"); return; }
    busyRef.current = true;
    const attempt = ++attemptRef.current;
    timerRef.current = setTimeout(() => {
      if (attempt !== attemptRef.current) return;
      attemptRef.current += 1;
      busyRef.current = false;
      setView("select");
      toast.error("A análise demorou mais que o esperado. Nenhuma confirmação foi enviada.");
    }, IMPORT_WAIT_MS);
    setSyncStock(false);
    setFileName(f.name); setView("busy");
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/product-imports/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (attempt !== attemptRef.current) return;
      setAnalysis(data); setMapping(data.mapping || {}); setView("preview");
      if (data.duplicado) toast.warning("Este arquivo aparentemente já foi importado anteriormente.");
    } catch (err) {
      if (attempt === attemptRef.current) { toast.error(apiErr(err)); setView("select"); }
    } finally {
      if (attempt === attemptRef.current) {
        clearTimeout(timerRef.current);
        busyRef.current = false;
        if (fileRef.current) fileRef.current.value = "";
      }
    }
  };

  const confirmImport = async () => {
    if (busyRef.current || !user) return;
    const saved = pendingRequest(user, CONFIRM_ENDPOINT);
    if (!saved && (!analysis || !mapping.codigo || !mapping.descricao)) return;
    const attempt = ++attemptRef.current;
    const current = () => attempt === attemptRef.current;
    busyRef.current = true;
    setView("busy");
    try {
      const request = saved || beginRequest(user, CONFIRM_ENDPOINT, {
        job_id: analysis.job_id, ignore_inactive: ignoreInactive, update_existing: updateExisting,
        sync_stock: syncStock && Boolean(mapping.estoque), mapping,
      });
      setPending(request);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (!current()) return;
        busyRef.current = false;
        setPending(request);
        setView("recovery");
      }, IMPORT_WAIT_MS);
      // This timeout releases the UI, not the backend operation. A late success
      // is accepted until a newer attempt or dialog lifecycle supersedes it.
      const { data } = await api.post(CONFIRM_ENDPOINT, request.payload);
      if (!current()) return;
      clearTimeout(timerRef.current);
      finishRequest(user, CONFIRM_ENDPOINT); setPending(null);
      busyRef.current = false;
      setResult(data); setView("result");
      // Refresh failures (or a refresh that never settles) cannot undo a receipt.
      Promise.resolve().then(() => onDone?.()).catch(() => {
        if (current()) toast.error("Importação concluída, mas não foi possível atualizar a lista de peças. Atualize a página.");
      });
    } catch (err) {
      if (!current()) return;
      clearTimeout(timerRef.current);
      busyRef.current = false;
      if (rejectedRequest(err)) {
        finishRequest(user, CONFIRM_ENDPOINT); setPending(null);
        setView(analysis ? "preview" : "select");
      } else {
        const unresolved = pendingRequest(user, CONFIRM_ENDPOINT);
        setPending(unresolved);
        setView(unresolved ? "recovery" : (analysis ? "preview" : "select"));
      }
      toast.error(apiErr(err));
    } finally {
      if (current()) { clearTimeout(timerRef.current); busyRef.current = false; }
    }
  };

  const openHistory = async () => { setView("history"); setHist(null); try { const { data } = await api.get("/product-imports/history"); setHist(data); } catch (e) { toast.error(apiErr(e)); } };
  const openDetail = async (id) => { setView("detail"); setDetail(null); try { const { data } = await api.get(`/product-imports/${id}`); setDetail(data); } catch (e) { toast.error(apiErr(e)); } };

  const cancelStaging = async () => { if (pendingRequest(user, CONFIRM_ENDPOINT)) return; if (analysis?.job_id && view === "preview") { try { await api.delete(`/product-imports/${analysis.job_id}`); } catch { /* ignore */ } } };
  const handleClose = (v) => { if (busyRef.current) return; if (!v) cancelStaging(); onOpenChange(v); };
  const downloadErrorsById = async (id) => {
    try {
      const token = localStorage.getItem("almox_token");
      const res = await fetch(`${API}/product-imports/${id}/errors.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "erros_importacao.csv"; a.click();
    } catch { toast.error("Falha ao baixar o relatório de erros."); }
  };
  const downloadFullErrors = () => downloadErrorsById(analysis.job_id);

  const r = analysis?.resumo;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Upload className="h-6 w-6 text-sky-400" /> Importar Produtos
            {view !== "history" && view !== "detail" && view !== "busy" && (
              <Button variant="ghost" size="sm" className="ml-auto text-slate-400" onClick={openHistory} data-testid="import-history-btn"><History className="h-4 w-4 mr-1" /> Histórico</Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {view === "select" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-400">Importe o cadastro de produtos exportado do <b>Omie</b> (CSV) ou uma planilha <b>Excel</b> (.xlsx/.xls). O sistema analisa o arquivo antes de importar.</p>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" onChange={onFile} data-testid="import-file-input" />
            <button onClick={pick} data-testid="import-pick-file" className="w-full rounded-2xl border-2 border-dashed border-slate-700 hover:border-sky-600 bg-slate-950/40 p-10 flex flex-col items-center gap-3 transition-colors">
              <FileSpreadsheet className="h-12 w-12 text-sky-400" />
              <span className="font-semibold text-slate-100">Selecionar arquivo</span>
              <span className="text-xs text-slate-500">Aceita .csv (Omie), .xlsx e .xls</span>
            </button>
          </div>
        )}

        {view === "recovery" && pending && (
          <div className="space-y-4 py-4" data-testid="import-recovery">
            <div className="flex items-center gap-2 text-amber-200"><AlertTriangle className="h-5 w-5" /> O resultado da última importação ainda não foi confirmado.</div>
            <p className="text-sm text-slate-300">A espera nesta tela não cancela o processamento no servidor. Algumas linhas podem já estar aplicadas e visíveis. Consulte novamente com os mesmos dados para retomar ou confirmar a conclusão, sem repetir produtos ou movimentos. Não envie o mesmo arquivo como uma nova importação enquanto esta estiver pendente.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => handleClose(false)}>Fechar</Button>
              <Button onClick={confirmImport} className="bg-sky-700 hover:bg-sky-600" data-testid="import-retry">Consultar resultado</Button>
            </div>
          </div>
        )}

        {view === "busy" && (
          <div className="py-16 flex flex-col items-center gap-3 text-slate-300">
            <Loader2 className="h-10 w-10 animate-spin text-sky-400" />
            <span data-testid="import-busy">Processando {fileName}...</span>
            <span className="text-xs text-slate-500">Isso pode levar alguns segundos para arquivos grandes.</span>
          </div>
        )}

        {view === "preview" && analysis && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="text-slate-400">Arquivo:</span> <b className="text-slate-100">{analysis.file_name}</b>
              <span className="text-slate-500 text-xs">{(analysis.size / 1024).toFixed(0)} KB · {analysis.total_linhas} linhas</span>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[["encontrados", "Encontrados", "text-slate-100"], ["novos", "Novos", "text-emerald-300"], ["existentes", "Existentes", "text-sky-300"], ["ativos", "Ativos", "text-slate-200"], ["inativos", "Inativos", "text-amber-300"], ["erros", "Com erro", "text-rose-300"]].map(([k, label, cls]) => (
                <div key={k} className="rounded-lg bg-slate-950/60 border border-slate-800 px-2 py-2 text-center">
                  <div className={`text-lg font-bold font-mono ${cls}`} data-testid={`import-count-${k}`}>{r[k]}</div>
                  <div className="text-[10px] uppercase text-slate-500 font-mono">{label}</div>
                </div>
              ))}
            </div>

            {(
              <div className="rounded-lg border border-amber-800/50 bg-amber-950/20 p-3">
                <div className="text-sm font-semibold text-amber-200 mb-2 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Mapeamento de colunas</div>
                <p className="text-xs text-amber-100/80 mb-2">Confira as colunas antes de confirmar. Campos ignorados ou vazios preservam os dados existentes. Código e descrição são obrigatórios:</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {["codigo", "descricao", "familia", "estoque", "unidade", ...(analysis.finance ? ["preco"] : []), "situacao"].map((field) => (
                    <div key={field} className="flex items-center gap-2 text-sm">
                      <span className="w-24 text-slate-300">{FIELD_LABELS[field]}</span>
                      <select data-testid={`import-map-${field}`} value={mapping[field] || ""} onChange={(e) => { setMapping({ ...mapping, [field]: e.target.value }); if (field === "estoque" && !e.target.value) setSyncStock(false); }} className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-100">
                        <option value="">— Ignorar —</option>
                        {analysis.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400">A prévia abaixo usa o mapeamento inicial. A confirmação valida novamente as colunas escolhidas.</p>
            <div className="overflow-x-auto rounded-lg border border-slate-800">
              <table className="w-full text-xs min-w-[640px]">
                <thead><tr className="bg-slate-950 text-slate-400 uppercase font-mono">
                  {["Código", "Descrição", "Família", "Estoque", "Un", ...(analysis.finance ? ["Preço"] : []), "Situação", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
                </tr></thead>
                <tbody data-testid="import-preview-table">
                  {analysis.preview.map((p, i) => (
                    <tr key={i} className={`border-t border-slate-800 ${p.error ? "bg-rose-950/20" : ""}`}>
                      <td className="px-3 py-1.5 font-mono text-sky-300">{p.codigo}</td>
                      <td className="px-3 py-1.5 text-slate-200">{p.descricao}</td>
                      <td className="px-3 py-1.5 text-slate-400">{p.familia || "—"}</td>
                      <td className="px-3 py-1.5 font-mono">{p.estoque ?? "—"}</td>
                      <td className="px-3 py-1.5">{p.unidade ?? "—"}</td>
                      {analysis.finance && <td className="px-3 py-1.5 font-mono">{p.preco ?? "—"}</td>}
                      <td className="px-3 py-1.5">{p.situacao || "—"}</td>
                      <td className="px-3 py-1.5">{p.error ? <span className="text-rose-400">{p.error}</span> : p.existente ? <span className="text-sky-400">existe</span> : <span className="text-emerald-400">novo</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-slate-200"><input type="checkbox" checked={ignoreInactive} onChange={(e) => setIgnoreInactive(e.target.checked)} className="h-4 w-4 accent-sky-500" data-testid="import-ignore-inactive" /> Ignorar produtos inativos</label>
              <label className="flex items-center gap-2 text-sm text-slate-200"><input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} className="h-4 w-4 accent-sky-500" data-testid="import-update-existing" /> Atualizar produtos existentes (somente campos mapeados e preenchidos)</label>
              <label className="flex items-center gap-2 text-sm text-amber-200"><input type="checkbox" checked={syncStock} disabled={!mapping.estoque} onChange={(e) => setSyncStock(e.target.checked)} className="h-4 w-4 accent-amber-500" data-testid="import-sync-stock" /> Sincronizar saldos com a coluna Estoque e registrar cada alteração no histórico</label>
              <p className="text-xs text-slate-400">Sem essa opção, os saldos atuais são preservados e produtos novos começam com saldo zero. Estoques e preços aceitam decimais finitos não negativos.</p>
              <p className="text-xs text-amber-200" data-testid="import-partial-notice">Durante a aplicação, linhas já gravadas podem ficar visíveis antes da conclusão do arquivo. Aguarde a conclusão antes de editar ou excluir os produtos importados. Em caso de falha, retome a mesma importação.</p>
            {syncStock && <p className="text-xs text-amber-200">A sincronização substitui os saldos dos produtos importados pelos valores do arquivo. Revise o arquivo antes de confirmar.</p>}
            </div>

            {analysis.errors_total > 0 && (
              <div className="flex items-center justify-between rounded-lg border border-rose-800/50 bg-rose-950/20 px-3 py-2 text-sm text-rose-200">
                <span>{analysis.errors_total} linha(s) com erro serão ignoradas.</span>
                <Button size="sm" variant="ghost" className="text-rose-300" onClick={downloadFullErrors}><Download className="h-4 w-4 mr-1" /> Baixar erros</Button>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
              <Button variant="ghost" onClick={async () => { await cancelStaging(); setView("select"); setAnalysis(null); }} data-testid="import-cancel">Cancelar</Button>
              <Button onClick={confirmImport} disabled={!mapping.codigo || !mapping.descricao} className="bg-sky-700 hover:bg-sky-600" data-testid="import-confirm"><CheckCircle2 className="h-4 w-4 mr-1" /> Confirmar importação</Button>
            </div>
          </div>
        )}

        {view === "result" && result && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-1 py-2"><CheckCircle2 className="h-12 w-12 text-emerald-400" /><div className="font-display text-xl font-bold" data-testid="import-result">Importação concluída</div><div className="text-xs text-slate-500">Duração: {result.duracao_ms} ms</div></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[["total", "Processados"], ["novos", "Novos"], ["atualizados", "Atualizados"], ["ignorados", "Ignorados"], ["inativos_ignorados", "Inativos ignorados"], ["erros", "Com erro"]].map(([k, label]) => (
                <div key={k} className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2 text-center"><div className="text-lg font-bold font-mono text-slate-100" data-testid={`import-result-${k}`}>{result[k]}</div><div className="text-[10px] uppercase text-slate-500 font-mono">{label}</div></div>
              ))}
            </div>
            {result.errors_total > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between"><span className="text-sm text-rose-300">{result.errors_total} erro(s)</span><Button size="sm" variant="ghost" className="text-rose-300" onClick={() => downloadErrorsById(result.id)}><Download className="h-4 w-4 mr-1" /> Baixar erros</Button></div>
                <div className="overflow-x-auto rounded-lg border border-slate-800 max-h-48"><table className="w-full text-xs"><thead><tr className="bg-slate-950 text-slate-400 uppercase font-mono"><th className="text-left px-3 py-2">Linha</th><th className="text-left px-3 py-2">Código</th><th className="text-left px-3 py-2">Produto</th><th className="text-left px-3 py-2">Erro</th></tr></thead><tbody>{result.errors.map((e, i) => (<tr key={i} className="border-t border-slate-800"><td className="px-3 py-1.5">{e.linha}</td><td className="px-3 py-1.5 font-mono">{e.codigo}</td><td className="px-3 py-1.5">{e.produto}</td><td className="px-3 py-1.5 text-rose-400">{e.erro}</td></tr>))}</tbody></table></div>
              </div>
            )}
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={openHistory}>Ver histórico</Button><Button onClick={() => onOpenChange(false)} className="bg-sky-700 hover:bg-sky-600">Concluir</Button></div>
          </div>
        )}

        {view === "history" && (
          <div className="space-y-3">
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => setView(pending ? "recovery" : (analysis ? "preview" : "select"))}><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
            {hist === null ? <Skeleton className="h-40 bg-slate-800" /> : hist.length === 0 ? <p className="text-slate-500 text-sm py-6 text-center">Nenhuma importação registrada.</p> : (
              <div className="overflow-x-auto rounded-lg border border-slate-800"><table className="w-full text-xs min-w-[640px]"><thead><tr className="bg-slate-950 text-slate-400 uppercase font-mono"><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Usuário</th><th className="text-left px-3 py-2">Arquivo</th><th className="text-right px-3 py-2">Proc.</th><th className="text-right px-3 py-2">Novos</th><th className="text-right px-3 py-2">Atual.</th><th className="text-right px-3 py-2">Ign.</th><th className="text-right px-3 py-2">Erros</th></tr></thead>
                <tbody data-testid="import-history-table">{hist.map((h) => (<tr key={h.id} className="border-t border-slate-800 hover:bg-slate-900/40 cursor-pointer" data-testid={`import-history-row-${h.id}`} onClick={() => openDetail(h.id)}><td className="px-3 py-1.5">{new Date(h.data).toLocaleString("pt-BR")}</td><td className="px-3 py-1.5">{h.usuario_nome}</td><td className="px-3 py-1.5 text-sky-300">{h.file_name}</td><td className="px-3 py-1.5 text-right font-mono">{h.total}</td><td className="px-3 py-1.5 text-right font-mono text-emerald-300">{h.novos}</td><td className="px-3 py-1.5 text-right font-mono text-sky-300">{h.atualizados}</td><td className="px-3 py-1.5 text-right font-mono text-slate-400">{h.ignorados}</td><td className="px-3 py-1.5 text-right font-mono text-rose-300">{h.erros}</td></tr>))}</tbody></table></div>
            )}
          </div>
        )}

        {view === "detail" && (
          <div className="space-y-3">
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={openHistory}><ArrowLeft className="h-4 w-4 mr-1" /> Voltar ao histórico</Button>
            {detail === null ? <Skeleton className="h-40 bg-slate-800" /> : (
              <>
                <div className="text-sm text-slate-300">{detail.importacao.file_name} · {new Date(detail.importacao.data).toLocaleString("pt-BR")} · {detail.itens.length} itens</div>
                <div className="overflow-x-auto rounded-lg border border-slate-800 max-h-80"><table className="w-full text-xs min-w-[640px]"><thead><tr className="bg-slate-950 text-slate-400 uppercase font-mono"><th className="text-left px-3 py-2">Código</th><th className="text-left px-3 py-2">Produto</th><th className="text-left px-3 py-2">Tipo</th><th className="text-right px-3 py-2">Estoque ant.</th><th className="text-right px-3 py-2">Estoque novo</th>{detail.finance && <><th className="text-right px-3 py-2">Preço ant.</th><th className="text-right px-3 py-2">Preço novo</th></>}</tr></thead>
                  <tbody>{detail.itens.map((it) => (<tr key={it.id} className="border-t border-slate-800"><td className="px-3 py-1.5 font-mono text-sky-300">{it.codigo}</td><td className="px-3 py-1.5">{it.descricao}</td><td className="px-3 py-1.5">{it.tipo === "novo" ? <span className="text-emerald-400">novo</span> : <span className="text-sky-400">atualizado</span>}</td><td className="px-3 py-1.5 text-right font-mono">{it.estoque_anterior}</td><td className="px-3 py-1.5 text-right font-mono">{it.estoque_novo}</td>{detail.finance && <><td className="px-3 py-1.5 text-right font-mono text-slate-400">{it.preco_anterior ?? "—"}</td><td className="px-3 py-1.5 text-right font-mono">{it.preco_novo ?? "—"}</td></>}</tr>))}</tbody></table></div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
